import { useLayoutEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { IconBell, IconChartBar, IconChecklist, IconDots, IconMail, IconMenu2, IconSearch, type Icon } from '@tabler/icons-react';
import { useShell } from './context';
import s from './shell.module.css';
import { useT } from '@hana/i18n';

/** Nút icon ở góc phải đầu trang (Mint-Job `dkHdr`): 42×42, badge số đỏ góc trên phải khi n > 0. */
export function HeaderTool({ label, icon: Ico, n, active, onClick }: { label: string; icon: Icon; n?: number; active?: boolean; onClick?: () => void }) {
  return (
    <button type="button" className={s.hb} aria-label={label} title={label} aria-pressed={active} onClick={onClick} style={{ position: 'relative' }}>
      <Ico size={18} stroke={1.9} />
      {!!n && <span className={s.hbN}>{n}</span>}
    </button>
  );
}

/** Cụm phải chuẩn của board danh sách: thống kê · việc · thư · chuông (badge) · ⋯. Màn truyền `n` theo dữ liệu thật. */
export function HeaderTools({ tasks, mail, noti, onMore }: { tasks?: number; mail?: number; noti?: number; onMore?: () => void }) {
  const t = useT();
  return (
    <>
      <HeaderTool label={t('shell.analytics')} icon={IconChartBar} />
      <HeaderTool label={t('shell.tasks')} icon={IconChecklist} n={tasks} />
      <HeaderTool label={t('shell.mailList')} icon={IconMail} n={mail} />
      <HeaderTool label={t('shell.noti')} icon={IconBell} n={noti} />
      <HeaderTool label={t('shell.more')} icon={IconDots} onClick={onMore} />
    </>
  );
}

/** Đầu trang của một màn: tiêu đề + một dòng phụ + nút; dính trên cùng, thu gọn khi cuộn (26 → 18px).
 *  `lead` đứng trước tiêu đề (nút ← quay lại + mã, màn chi tiết); `meta` đứng ngay sau tên trên cùng dòng (chip trạng thái), dòng phụ nằm dưới cả hai.
 *  `crumb` (breadcrumb "Trang chủ › Màn", 12px/550) nằm TRÊN tiêu đề (Mint-Tasks `nav[aria-label=Vị trí]`).
 *  `tabs` (nhóm tab, như Mint-Job) nằm cạnh tiêu đề, bên trái; ẩn trên điện thoại (màn tự đặt tab dưới đầu trang).
 *  Tìm nhanh (Ctrl K) có sẵn; `findHint={false}` bỏ chữ "Ctrl K" cạnh icon (board Mint-TMS-Trip chỉ có icon 40×40); `actions` là cụm nút bên phải của màn — nút chủ chốt (mực) đặt cuối, mỗi thanh một nút.
 *  Đo chiều cao thật (ResizeObserver) và đặt `--page-head-h` (px) trên `main`; thanh dính của màn dùng `top: var(--page-head-h)`.
 *  `dense` = đầu trang chi tiết của board Mint-BLEdit (padding 18 / 12, hàng đầu cao 40, tiêu đề 24px, dòng phụ cách 6px); `noFind` bỏ nút Tìm nhanh (board không có). Khổ iPad mặc định KHÔNG có nút Tìm nhanh (42/49 board iPad không có); `findPad` bật lại cho màn mà board iPad có (BL, Home, Import, TMS…). `minH` = chiều cao tối thiểu của hàng đầu trang (board `hdrH`, vd Mint-HS 52 khi chưa thu gọn; Mint-Job 42) — không đặt thì theo nội dung.
 *  `actGap` = gom nút Tìm nhanh + actions vào một cụm cách nhau N px (board Job: 8; mặc định theo gap 12 của dải).
 *  Vùng cuộn: `useScrollRoot()`. */
export function PageHeader({ title, sub, crumb, lead, meta, tabs, actions, findHint = true, dense = false, noFind = false, findPad = false, minH, actGap }: { title: string; sub?: ReactNode; crumb?: ReactNode; lead?: ReactNode; meta?: ReactNode; tabs?: ReactNode; actions?: ReactNode; findHint?: boolean; dense?: boolean; noFind?: boolean; findPad?: boolean; minH?: number; actGap?: number }) {
  const t = useT();
  const { layout, condensed, openFind, openNav, findButton } = useShell();
  const phone = layout === 'phone';
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    const root = el?.closest('main');
    if (!el || !root) return;
    const set = () => root.style.setProperty('--page-head-h', `${Math.round(el.getBoundingClientRect().height)}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty('--page-head-h');
    };
  }, []);
  return (
    <div ref={ref} className={`${s.hdr} ${dense ? s.dense : ''} ${condensed && !phone ? s.cond : ''}`} style={minH && !phone ? ({ '--hdr-min': `${minH}px` } as CSSProperties) : undefined}>
      {layout === 'pad' && (
        <button type="button" className={s.hb} aria-label={t('shell.openMenu')} title={t('shell.openMenu')} onClick={openNav}>
          <IconMenu2 size={20} />
        </button>
      )}
      {lead && <div className={s.hLead}>{lead}</div>}
      <div className={s.hTx}>
        {crumb && <nav className={s.hCrumb} aria-label={t('shell.crumb')}>{crumb}</nav>}
        {meta || dense ? (
          <div className={s.hName}>
            <h1 className={s.h1}>{title}</h1>
            {meta && <div className={s.hMeta}>{meta}</div>}
          </div>
        ) : (
          <h1 className={s.h1}>{title}</h1>
        )}
        {sub && <div className={s.hSub}>{sub}</div>}
      </div>
      {tabs && !phone && <div className={s.hTabs}>{tabs}</div>}
      {actGap !== undefined && !phone ? <div className={s.hAct} style={{ gap: actGap }}>
      {!phone && findButton && !noFind && (layout !== 'pad' || findPad) &&
        (layout === 'pad' || !findHint ? (
          <button type="button" className={s.hb} aria-label={layout === 'pad' ? t('common.search') : t('shell.findK')} title={layout === 'pad' ? t('common.search') : t('shell.findKt')} onClick={openFind}>
            <IconSearch size={layout === 'pad' ? 19 : 16} stroke={layout === 'pad' ? undefined : 2.2} />
          </button>
        ) : (
          <button type="button" className={`${s.hb} ${s.hbFind}`} aria-label={t('shell.findK')} title={t('shell.findKt')} onClick={openFind}>
            <IconSearch size={16} stroke={2.2} />
            <span className={s.kbdTx}>Ctrl K</span>
          </button>
        ))}
      {actions}
      </div> : <>
      {!phone && findButton && !noFind && (layout !== 'pad' || findPad) &&
        (layout === 'pad' || !findHint ? (
          <button type="button" className={s.hb} aria-label={layout === 'pad' ? t('common.search') : t('shell.findK')} title={layout === 'pad' ? t('common.search') : t('shell.findKt')} onClick={openFind}>
            <IconSearch size={layout === 'pad' ? 19 : 16} stroke={layout === 'pad' ? undefined : 2.2} />
          </button>
        ) : (
          <button type="button" className={`${s.hb} ${s.hbFind}`} aria-label={t('shell.findK')} title={t('shell.findKt')} onClick={openFind}>
            <IconSearch size={16} stroke={2.2} />
            <span className={s.kbdTx}>Ctrl K</span>
          </button>
        ))}
      {actions}
      </>}
    </div>
  );
}
